/**
 * CR-03 discovery and CR-04 campaign detail (FR-CAM-009, FR-CAM-010,
 * FR-PAR-001/004).
 *
 * Two rules from the requirements drive most of what is on screen.
 *
 * Capacity shown is a snapshot and says so. FR-CAM-010 is explicit that it
 * may change before joining and that the server's answer at join time is the
 * authoritative one, so the number is labelled "at the last check" rather
 * than presented as a promise, and a full campaign is reported by the join
 * attempt, not predicted by the card.
 *
 * An ineligible campaign is still listed, with its reason. Filtering it out
 * silently is worse than useless: a creator who cannot see why a campaign
 * vanished concludes the platform is broken, and the reasons are written to
 * say what is true without naming another party's private criteria
 * (FR-CAM-003).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Eyebrow, LinkButton, StateBlock, Badge,
} from "@ui/primitives";
import { TextInput, Select } from "@ui/forms";
import { useToast } from "@app/shared/Toasts";
import { formatMoney, fromMinorString } from "@contracts/money";
import { fontSize, space } from "@design/index";
import { api, type ApiResult } from "@app/shared/api";
import {
  CampaignTile, campaignBadge, campaignModeLabel, campaignRateLabel,
} from "@app/shared/CampaignTile";
import { CampaignGridSkeleton } from "@app/shared/Skeletons";
import {
  AssetBoard, BriefColumn, CampaignDetailSkeleton, CampaignSectionTabs, CampaignShowcase,
  ScheduleBoard, type CampaignSectionId,
} from "@app/shared/CampaignShowcase";
import {
  creatorPlatformRates, lowestPlatformRate, PlatformRateTiles, type StoredPlatformRate,
} from "@app/shared/platformRates";
import { campaignCoverUrl } from "@app/shared/coverUrl";

export interface DiscoverCampaign {
  campaign_id: string;
  display_name: string;
  brand_name: string;
  summary: string;
  content_category: string | null;
  mode: string;
  currency: string;
  cpm_gross_minor: string;
  per_creator_cap_minor: string;
  join_opens_at?: string | null;
  join_closes_at: string | null;
  submission_deadline_at: string | null;
  campaign_ends_at?: string | null;
  measurement_routes: string[];
  remaining_capacity_minor: string;
  eligible: boolean | null;
  created_at: string;
  cover_asset_id?: string | null;
  platform_rates?: StoredPlatformRate[] | null;
}

interface Gate { gate: string; passed: boolean; detail: string }

interface CampaignDetail {
  campaign_id: string;
  display_name: string;
  brand_name: string;
  brand_website: string | null;
  summary: string;
  objective: string | null;
  content_category: string | null;
  campaign_type: string | null;
  brief: Record<string, unknown>;
  eligibility: Record<string, unknown>;
  mode: string;
  currency: string;
  cpm_gross_minor: string;
  per_creator_cap_minor: string;
  per_post_cap_minor: string | null;
  submissions_per_creator: number;
  display_timezone: string;
  join_opens_at?: string | null;
  join_closes_at: string | null;
  submission_deadline_at: string | null;
  campaign_ends_at?: string | null;
  tracking_days: number;
  measurement_cutoff_at: string | null;
  min_live_days: number;
  earning_hold_days: number;
  measurement_routes: string[];
  remaining_capacity_minor: string;
  capacity_is_indicative: boolean;
  version: number;
  assets: { slot: string; kind: string; filename: string }[];
  cover_asset_id?: string | null;
  platform_rates?: StoredPlatformRate[] | null;
}

interface Participation {
  id: string; state: string; gross_cap_minor: string;
  expires_at: string | null; accepted_at: string | null;
}

const minor = (v: string, ccy: string) =>
  fromMinorString(v, ccy as "GBP" | "USD" | "EUR");

const asDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "-";

function daysUntil(iso: string | null): string {
  if (!iso) return "no deadline set";
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "closed";
  if (days === 0) return "closes today";
  return days === 1 ? "1 day left" : `${days} days left`;
}

const messageOf = (r: Extract<ApiResult<unknown>, { ok: false }>) => r.message;

/* --------------------------------------------------------------- filters */

interface Filters {
  q: string; sort: string; category: string; minCpm: string;
}

const EMPTY: Filters = { q: "", sort: "newest", category: "", minCpm: "" };

const PAGE = 100;

function toQuery(f: Filters, offset = 0): string {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set("q", f.q.trim());
  if (f.sort !== "newest") p.set("sort", f.sort);
  if (f.category) p.set("category", f.category);
  if (f.minCpm) p.set("min_cpm", String(Math.round(Number(f.minCpm) * 100)));
  p.set("limit", String(PAGE));
  if (offset > 0) p.set("offset", String(offset));
  return p.toString();
}

/* ------------------------------------------------------------ CR-03 list */

export function CampaignBrowse({ onOpen }: { onOpen: (id: string) => void }) {
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [rows, setRows] = useState<DiscoverCampaign[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (f: Filters, offset = 0) => {
    setError(null);
    const r = await api.get<{ campaigns: DiscoverCampaign[]; total: number }>(
      `/campaigns?${toQuery(f, offset)}`);
    if (r.ok) {
      setRows((prev) => offset === 0 ? r.data.campaigns : [...(prev ?? []), ...r.data.campaigns]);
      setTotal(r.data.total);
    } else {
      setError(messageOf(r));
      if (offset === 0) setRows([]);
    }
  }, []);

  useEffect(() => { void load(applied, 0); }, [load, applied]);

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((prev) => ({ ...prev, [k]: v }));

  async function loadMore() {
    setLoadingMore(true);
    await load(applied, rows?.length ?? 0);
    setLoadingMore(false);
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card padding={16} style={{ display: "grid", gap: space(3) }}>
        <form
          className="cm-discover-filters"
          onSubmit={(e) => { e.preventDefault(); setApplied(filters); }}
          style={{ display: "grid", gap: space(3),
                   gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                   alignItems: "end" }}
        >
          <TextInput
            label="Search"
            placeholder="Brand, campaign or brief"
            value={filters.q}
            onChange={(e) => set("q", e.currentTarget.value)}
          />
          <Select label="Sort" value={filters.sort} onChange={(e) => set("sort", e.currentTarget.value)}>
            <option value="newest">Newest</option>
            <option value="deadline">Closing soonest</option>
            <option value="cpm">Highest CPM</option>
          </Select>
          <TextInput
            label="Min CPM"
            inputMode="decimal"
            placeholder="0.00"
            value={filters.minCpm}
            onChange={(e) => set("minCpm", e.currentTarget.value)}
          />
          <div style={{ display: "flex", gap: space(2) }}>
            <Button type="submit">Apply</Button>
            {JSON.stringify(applied) !== JSON.stringify(EMPTY) ? (
              <Button variant="ghost" onClick={() => { setFilters(EMPTY); setApplied(EMPTY); }}>
                Clear
              </Button>
            ) : null}
          </div>
        </form>
      </Card>

      {error ? <StateBlock kind="error" title="Could not load campaigns" detail={error} /> : null}

      {rows === null ? (
        <CampaignGridSkeleton count={8} />
      ) : rows.length === 0 ? (
        <StateBlock
          kind="empty"
          title="Nothing matches those filters"
          detail="Try a broader search, or clear the filters to see everything currently open."
        />
      ) : (
        <>
          <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--muted)" }}>
            {total} campaign{total === 1 ? "" : "s"}
          </p>
          <div className="cm-campaign-grid" style={{ display: "grid", gap: "clamp(10px, 1.2vw, 16px)",
                        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
            {rows.map((c) => <CampaignCard key={c.campaign_id} c={c} onOpen={onOpen} />)}
          </div>
          {rows.length < total ? (
            <div>
              <Button variant="secondary" disabled={loadingMore} onClick={() => void loadMore()}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export function CampaignCard({ c, onOpen }: { c: DiscoverCampaign; onOpen: (id: string) => void }) {
  const rates = creatorPlatformRates({
    routes: c.measurement_routes,
    rates: c.platform_rates,
    fallbackCpm: c.cpm_gross_minor,
  });
  const lowest = rates.length > 1 ? lowestPlatformRate(rates) : null;
  return (
    <CampaignTile
      brand={c.brand_name}
      name={c.display_name}
      rateLabel={lowest
        ? `Starting from ${campaignRateLabel(lowest, c.currency)}`
        : campaignRateLabel(c.cpm_gross_minor, c.currency)}
      badge={campaignBadge({
        eligible: c.eligible, joinClosesAt: c.join_closes_at, joinOpensAt: c.join_opens_at,
      })}
      platforms={c.measurement_routes}
      img={c.cover_asset_id ? campaignCoverUrl(c.campaign_id, c.cover_asset_id) : undefined}
      href={`/campaigns/${c.campaign_id}`}
      onOpen={() => onOpen(c.campaign_id)}
    />
  );
}

/* ----------------------------------------------------------- CR-04 detail */

export function CampaignDetailScreen({ id, onBack, onJoined }: {
  id: string; onBack: () => void; onJoined: (participationId: string) => void;
}) {
  const [data, setData] = useState<{
    campaign: CampaignDetail; eligibility: Gate[] | null; participation: Participation | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState<CampaignSectionId>("general");
  const toast = useToast();

  const load = useCallback(async () => {
    setError(null);
    const r = await api.get<{
      campaign: CampaignDetail; eligibility: Gate[] | null; participation: Participation | null;
    }>(`/campaigns/${id}`);
    if (r.ok) setData(r.data); else setError(messageOf(r));
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setSection("general"); }, [id]);

  const blocked = useMemo(
    () => (data?.eligibility ?? []).filter((g) => !g.passed && g.gate !== "capacity_available"),
    [data],
  );

  const join = useCallback(async () => {
    setBusy(true);
    const r = await api.post<{ participationId: string }>(`/campaigns/${id}/join`);
    setBusy(false);
    if (r.ok) {
      toast.ok("You have joined this campaign.");
      onJoined(r.data.participationId);
    } else {
      toast.error(messageOf(r));
      void load();
    }
  }, [id, onJoined, load, toast]);

  if (error) {
    return (
      <>
        <StateBlock kind="error" title="Could not load this campaign" detail={error} />
        <div style={{ marginTop: space(3) }}>
          <Button variant="secondary" onClick={onBack}>Back to home</Button>
        </div>
      </>
    );
  }
  if (!data) return <CampaignDetailSkeleton />;

  const c = data.campaign;
  const ccy = c.currency;
  const rates = creatorPlatformRates({
    routes: c.measurement_routes,
    rates: c.platform_rates,
    fallbackCpm: c.cpm_gross_minor,
  });
  const badge = campaignBadge({
    eligible: blocked.length === 0 ? true : null,
    joinClosesAt: c.join_closes_at,
    joinOpensAt: c.join_opens_at,
  });
  const summary = c.summary.trim().length >= 8 ? c.summary.trim() : null;
  const schedule = [
    ...(c.join_opens_at ? [{ label: "Opening date", value: asDate(c.join_opens_at) }] : []),
    { label: "Content submission", value: `${asDate(c.submission_deadline_at)} · ${daysUntil(c.submission_deadline_at)}` },
    { label: "Campaign end", value: asDate(c.campaign_ends_at ?? c.measurement_cutoff_at) },
    { label: "Stay live", value: `${c.min_live_days} days` },
    { label: "Tracked for", value: `${c.tracking_days} days per post` },
    { label: "Earnings held", value: `${c.earning_hold_days} days after release` },
  ];

  return (
    <div style={{ display: "grid", gap: 22 }}>
      <a
        href="/"
        onClick={(e) => { e.preventDefault(); onBack(); }}
        style={{
          fontSize: fontSize.bodyLg, color: "var(--link)", textDecoration: "none",
          fontWeight: 600, display: "inline-block",
        }}
      >
        ← All campaigns
      </a>
      <CampaignSectionTabs value={section} onChange={setSection} />
      {section === "brief" ? (
        <div id="campaign-panel-brief" role="tabpanel" aria-labelledby="campaign-tab-brief">
          <BriefColumn brief={c.brief} />
        </div>
      ) : (
        <div id="campaign-panel-general" role="tabpanel" aria-labelledby="campaign-tab-general" style={{ display: "grid", gap: 22 }}>
          <CampaignShowcase
            showBack={false}
            backHref="/"
            backLabel="All campaigns"
            onBack={onBack}
            brand={c.brand_name}
            {...(data.participation?.state === "LIVE"
              ? { brandTrailing: <Badge tone="ok">Joined</Badge> }
              : {})}
            name={c.display_name}
            summary={summary}
            badge={badge}
            coverUrl={c.cover_asset_id ? campaignCoverUrl(c.campaign_id, c.cover_asset_id) : null}
            stats={[
              { label: "Cap", value: formatMoney(minor(c.per_creator_cap_minor, ccy)), hint: c.measurement_routes.filter((r) => r && r !== "sandbox").length > 1 ? "Maximum on each required platform" : "Maximum, not a promise" },
              { label: "Submit by", value: asDate(c.submission_deadline_at), hint: daysUntil(c.submission_deadline_at) },
              { label: "Ends", value: asDate(c.campaign_ends_at ?? c.measurement_cutoff_at), hint: "Review and earnings start" },
              { label: "Posts", value: c.submissions_per_creator, hint: campaignModeLabel(c.mode) },
            ]}
            afterStats={rates.length > 0 ? <PlatformRateTiles rows={rates} currency={ccy} /> : null}
            actions={
              <HeroActions
                campaign={c}
                blocked={blocked}
                participation={data.participation}
                busy={busy}
                onJoin={() => void join()}
              />
            }
          />
          <div className="cm-general-stack">
            <ActionCard
              blocked={blocked}
              participation={data.participation}
            />
            <ScheduleBoard
              rows={schedule}
              info={`Dates in ${c.display_timezone}. Capacity is rechecked when you join.`}
            />
            <AssetBoard assets={c.assets} />
          </div>
        </div>
      )}
    </div>
  );
}

function HeroActions({ campaign, blocked, participation, busy, onJoin }: {
  campaign: CampaignDetail;
  blocked: Gate[];
  participation: Participation | null;
  busy: boolean;
  onJoin: () => void;
}) {
  if (participation && ["LIVE", "OFFERED"].includes(participation.state)) {
    return (
      <LinkButton href={`/work/${campaign.campaign_id}`} variant="primary">
        {participation.state === "LIVE" ? "Open in My Campaigns" : "Review offer"}
      </LinkButton>
    );
  }
  if (blocked.length > 0) return null;
  return (
    <Button variant="primary" onClick={onJoin} disabled={busy}>
      {busy ? "Joining…" : "Join now"}
    </Button>
  );
}

/** The one card that decides what this creator can do right now. */
function ActionCard({ blocked, participation }: {
  blocked: Gate[];
  participation: Participation | null;
}) {
  if (participation && ["LIVE", "OFFERED"].includes(participation.state)) return null;

  if (blocked.length === 0) return null;

  return (
    <Card padding={18} style={{ display: "grid", gap: space(2) }}>
      <Eyebrow>Why you cannot join</Eyebrow>
      <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: space(1),
                   fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
        {blocked.map((g) => <li key={g.gate}>{g.detail}</li>)}
      </ul>
    </Card>
  );
}
